import styles from './EmailTemplatePicker.module.scss';

import PinIcon from '@mui/icons-material/PushPin';
import { Radio, Typography } from '@mui/material';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IResponse } from '@work4all/data';

import { EMailTemplate } from '@work4all/models/lib/Classes/EMailTemplate.entity';
import { EMailTemplateKind } from '@work4all/models/lib/Enums/EMailTemplateKind.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Tooltip } from '../../tooltip/Tooltip';
import { TabsConfiguration } from '../components/v2/EntityPickerTabbed';
import {
  EntityPickerListProps,
  PickerBaseTabbed,
} from '../components/v2/PickerBase';
import { useEntityPickerFilterResult } from '../components/v2/utils/hooks/use-entity-picker-filter-result';
import { useEntityPickerRegularResult } from '../components/v2/utils/hooks/use-entity-picker-regular-result';
import { IPickerWithTabsProps, StandardItems } from '../types';

export type EmailTemplatePickerProps = Omit<
  IPickerWithTabsProps<EMailTemplate, false>,
  'multiple'
> & {
  className?: string;
  noTemplate?: boolean;
  isGeneralEmailSource?: boolean;
  initialTemplateKind?: EMailTemplateKind;
  eMailTemplateKind?: EMailTemplateKind;
  standardTemplates: StandardItems<EMailTemplateKind>;
  setStandardTemplates: (value: StandardItems<EMailTemplateKind>) => void;
};

export function EmailTemplatePicker(props: EmailTemplatePickerProps) {
  const {
    value,
    data: initData,
    prefilter: initPrefilter,
    onChange,
    isGeneralEmailSource,
    noTemplate,
    initialTemplateKind,
    standardTemplates,
    setStandardTemplates,
    ...rest
  } = props;

  const { t } = useTranslation();

  const [activeTabIndex, setActiveTabIndex] = useState(() => {
    if (!getTabIndex || !value) return 0;
    return getTabIndex(value);
  });

  const templateKind = initialTemplateKind ?? props?.value?.eMailTemplateKind;

  const isGeneralTemplate =
    isGeneralEmailSource ||
    (initialTemplateKind && initialTemplateKind === EMailTemplateKind.KEINE);

  const [query, setQuery] = useState('');
  const queryTrimmed = query.trim();

  const getPrefilter = useCallback(
    (kind?: EMailTemplateKind) => {
      let prefilter = [
        { $or: [{ parentId: { $eq: null } }, { languageCode: { $eq: 0 } }] },
      ];

      if (initPrefilter) {
        prefilter = [...(initPrefilter as any), ...prefilter];
      }
      if (!kind) return prefilter;

      const kindFilter = { eMailTemplateKind: { $eq: kind } };
      return [...prefilter, { $and: [kindFilter] }];
    },
    [initPrefilter]
  );

  const allFields = useMemo(() => {
    return { ...EMAIL_TEMPLATE_PICKER_DATA, ...initData };
  }, [initData]);

  const handleStandardChange = useCallback(
    (template: EMailTemplate) => {
      if (template.eMailTemplateKind) {
        setStandardTemplates({
          ...standardTemplates,
          [template.eMailTemplateKind]:
            standardTemplates[template.eMailTemplateKind] !== template.id
              ? template.id
              : null,
        });
      }
    },
    [setStandardTemplates, standardTemplates]
  );

  const validateStandard = useCallback(
    (template: EMailTemplate) => {
      if (standardTemplates[template.eMailTemplateKind] === template.id) {
        return true;
      }
      return false;
    },
    [standardTemplates]
  );

  const renderItem = useCallback((item: EMailTemplate) => {
    return (
      <Tooltip
        title={item.name ?? ''}
        enterDelay={1000}
        enterNextDelay={1000}
        showOnlyOnOverflow
      >
        <Typography
          variant="body2"
          noWrap
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {item.name}
        </Typography>
      </Tooltip>
    );
  }, []);

  const loadingConfig = useMemo(
    () => ({
      entity: Entities.eMailTemplate,
      data: allFields,
      sortBy: 'name',
      filterBy: ['name'],
    }),
    [allFields]
  );

  const renderRadio = useCallback(
    (value: EMailTemplate) =>
      Number(value?.id) !== 0 && (
        <Tooltip title={t('INPUTS.SET_STANDART')} placement="right">
          <Radio
            icon={<PinIcon className={styles.pinIcon} />}
            checkedIcon={
              <PinIcon
                className={clsx(styles.pinIcon, styles.checkedPinIcon)}
              />
            }
            checked={validateStandard(value)}
            onClick={(e) => {
              e.stopPropagation();
              handleStandardChange(value);
            }}
            value={value.id}
            sx={{
              visibility: validateStandard(value) && 'visible',
            }}
          />
        </Tooltip>
      ),
    [handleStandardChange, t, validateStandard]
  );

  const defaultPickerConfig = useMemo(
    () => ({
      placeholder: t('INPUTS.SEARCH'),
      renderItemContent: renderItem,
      renderRadio,
    }),
    [renderItem, renderRadio, t]
  );

  const tabs = useMemo(
    (): TabsConfiguration<EMailTemplate>[] => [
      {
        loadingConfig,
        pickerConfig: {
          ...defaultPickerConfig,
          label: t('COMMON.GENERAL'),
        },
      },
      {
        loadingConfig,
        pickerConfig: {
          ...defaultPickerConfig,
          label: t('COMMON.TICKETS'),
          disabled:
            !isGeneralTemplate &&
            [
              EMailTemplateKind.APPOINTMENT_NOTIFICATION,
              EMailTemplateKind.ERP_OBJECTS,
            ].includes(templateKind),
        },
      },
      {
        loadingConfig,
        pickerConfig: {
          ...defaultPickerConfig,
          label: t('COMMON.APPOINTMENTS'),
          disabled:
            !isGeneralTemplate &&
            [EMailTemplateKind.TICKET, EMailTemplateKind.ERP_OBJECTS].includes(
              templateKind
            ),
        },
      },
      {
        loadingConfig,
        pickerConfig: {
          ...defaultPickerConfig,
          label: t('COMMON.ERP'),
          disabled:
            !isGeneralTemplate &&
            [
              EMailTemplateKind.TICKET,
              EMailTemplateKind.APPOINTMENT_NOTIFICATION,
            ].includes(templateKind),
        },
      },
    ],
    [defaultPickerConfig, isGeneralTemplate, loadingConfig, t, templateKind]
  );

  const activeTabConfig = tabs[activeTabIndex];
  const {
    entity,
    data,
    filterConfig,
    sortBy,
    sortByDirection,
    filterBy,
    searchPrefilter,
    keysArguments,
    vars,
    useSearchHistory,
    transformResponse,
  } = activeTabConfig.loadingConfig;

  const eMailTemplateKind = getEMailKindByTabIndex(activeTabIndex);
  const prefilter = getPrefilter(eMailTemplateKind);

  const regularResult = useEntityPickerRegularResult({
    entity,
    data,
    filterConfig,
    sortBy,
    sortByDirection,
    filterBy,
    query: queryTrimmed,
    prefilter,
    searchPrefilter,
    keysArguments,
    vars,
    useSearchHistory,
    transformResponse,
  });

  const filterResult = useEntityPickerFilterResult({
    entity,
    data,
    filterConfig,
    sortBy,
    sortByDirection,
    filterBy,
    query: queryTrimmed,
  });

  const additionalItems = [
    { id: 0, name: t('COMMON.BLANK') },
  ] as unknown as EMailTemplate[];

  const mappedRegularResults = useMemo(
    () => {
      const eMailTemplateKindData = regularResult?.data ?? [];

      const sortedData = [...eMailTemplateKindData].sort((a, b) => {
        if (a.id === standardTemplates[a.eMailTemplateKind]) return -1;
        if (b.id === standardTemplates[a.eMailTemplateKind]) return 1;
        return a.name.localeCompare(b.name);
      });
      const standardTemplateIndex = sortedData.findIndex(
        (template) => template.id === standardTemplates[templateKind]
      );

      if (noTemplate) {
        if (standardTemplateIndex !== -1) {
          sortedData.splice(standardTemplateIndex + 1, 0, ...additionalItems);
        } else {
          sortedData.splice(0, 0, ...additionalItems);
        }
      }

      return {
        ...regularResult,
        total: sortedData.length,
        data: sortedData,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      noTemplate,
      initialTemplateKind,
      regularResult?.data,
      regularResult?.loading,
    ]
  );

  const tabsPickerConfigs = useMemo(
    (): EntityPickerListProps<EMailTemplate>[] =>
      tabs.map((el) => {
        const config: EntityPickerListProps<EMailTemplate> = {
          ...el.pickerConfig,
          entity: el.loadingConfig.entity,
          filterBy: el.loadingConfig.filterBy,
          regularResult: null,
          filterResult: null,
        };

        return config;
      }),
    [tabs]
  );

  tabsPickerConfigs[activeTabIndex].regularResult =
    mappedRegularResults as IResponse<EMailTemplate>;
  tabsPickerConfigs[activeTabIndex].filterResult = filterResult;

  return (
    <PickerBaseTabbed
      onChange={onChange}
      value={value}
      {...rest}
      {...activeTabConfig.pickerConfig}
      tabs={tabsPickerConfigs}
      filterConfig={activeTabConfig.loadingConfig.filterConfig}
      getTabIndex={getTabIndex}
      onTabChange={(idx) => {
        setActiveTabIndex(idx);
      }}
      onSearchValueChange={(qry) => {
        setQuery(qry);
      }}
    />
  );
}

export const EMAIL_TEMPLATE_PICKER_DATA: EMailTemplate = {
  id: null,
  name: null,
  body: null,
  subject: null,
  languageCode: null,
  parentId: null,
  eMailTemplateKind: null,
  childs: [
    {
      id: null,
      body: null,
      subject: null,
      languageCode: null,
      parentId: null,
      signature: {
        id: null,
        name: null,
        body: null,
        bodyTextmarkenAufgeloestInlineImagesResolved: null,
      },
      attachmentList: [
        {
          id: null,
        },
      ],
    },
  ],
  signature: {
    body: null,
    bodyTextmarkenAufgeloestInlineImagesResolved: null,
    id: null,
    name: null,
  },
  attachmentList: [
    {
      id: null,
    },
  ],
};

function getTabIndex(template: EMailTemplate): number {
  switch (template.eMailTemplateKind) {
    case EMailTemplateKind.TICKET:
      return 1;
    case EMailTemplateKind.APPOINTMENT_NOTIFICATION:
      return 2;
    case EMailTemplateKind.ERP_OBJECTS:
      return 3;
    default:
      return 0;
  }
}
function getEMailKindByTabIndex(idx: number): EMailTemplateKind {
  switch (idx) {
    case 1:
      return EMailTemplateKind.TICKET;
    case 2:
      return EMailTemplateKind.APPOINTMENT_NOTIFICATION;
    case 3:
      return EMailTemplateKind.ERP_OBJECTS;
    default:
      return EMailTemplateKind.KEINE;
  }
}
